import React from "react";
import { Sparkles } from "lucide-react";
import { useRecommendations } from "../context/RecommendationsContext";

// Vibrant per-sign visual identity
const SIGN_THEME = {
  "Aries":       { gradient: "from-rose-500 to-red-600",         glow: "rgba(244,63,94,0.45)",   element: "Fuego" },
  "Tauro":       { gradient: "from-emerald-500 to-teal-600",     glow: "rgba(20,184,166,0.45)",  element: "Tierra" },
  "Géminis":     { gradient: "from-amber-400 to-orange-500",     glow: "rgba(251,146,60,0.45)",  element: "Aire" },
  "Geminis":     { gradient: "from-amber-400 to-orange-500",     glow: "rgba(251,146,60,0.45)",  element: "Aire" },
  "Cáncer":      { gradient: "from-pink-400 to-rose-500",        glow: "rgba(244,114,182,0.45)", element: "Agua" },
  "Cancer":      { gradient: "from-pink-400 to-rose-500",        glow: "rgba(244,114,182,0.45)", element: "Agua" },
  "Leo":         { gradient: "from-orange-500 to-red-600",       glow: "rgba(249,115,22,0.45)",  element: "Fuego" },
  "Virgo":       { gradient: "from-lime-500 to-emerald-600",     glow: "rgba(132,204,22,0.45)",  element: "Tierra" },
  "Libra":       { gradient: "from-sky-400 to-cyan-500",         glow: "rgba(14,165,233,0.45)",  element: "Aire" },
  "Escorpio":    { gradient: "from-purple-600 to-fuchsia-700",   glow: "rgba(147,51,234,0.55)",  element: "Agua" },
  "Sagitario":   { gradient: "from-fuchsia-500 to-purple-600",   glow: "rgba(217,70,239,0.45)",  element: "Fuego" },
  "Capricornio": { gradient: "from-amber-700 to-stone-800",      glow: "rgba(120,113,108,0.45)", element: "Tierra" },
  "Acuario":     { gradient: "from-cyan-400 to-blue-600",        glow: "rgba(14,165,233,0.45)",  element: "Aire" },
  "Piscis":      { gradient: "from-violet-500 to-pink-500",      glow: "rgba(168,85,247,0.45)",  element: "Agua" },
};

// Modern vector illustrations for each sign (pure SVG, no emoji)
function ZodiacIcon({ name, gradient }) {
  const id = `zg-${name.replace(/[^a-z]/gi, "")}`;
  const grads = {
    "from-rose-500 to-red-600":      ["#f43f5e", "#dc2626"],
    "from-emerald-500 to-teal-600":  ["#10b981", "#0d9488"],
    "from-amber-400 to-orange-500":  ["#fbbf24", "#f97316"],
    "from-pink-400 to-rose-500":     ["#f472b6", "#f43f5e"],
    "from-orange-500 to-red-600":    ["#f97316", "#dc2626"],
    "from-lime-500 to-emerald-600":  ["#84cc16", "#059669"],
    "from-sky-400 to-cyan-500":      ["#38bdf8", "#06b6d4"],
    "from-purple-600 to-fuchsia-700":["#9333ea", "#a21caf"],
    "from-fuchsia-500 to-purple-600":["#d946ef", "#9333ea"],
    "from-amber-700 to-stone-800":   ["#b45309", "#1c1917"],
    "from-cyan-400 to-blue-600":     ["#22d3ee", "#2563eb"],
    "from-violet-500 to-pink-500":   ["#8b5cf6", "#ec4899"],
  };
  const [c1, c2] = grads[gradient] || ["#1d6cb1", "#0e4a7a"];

  // Each sign uses a distinct minimal SVG path
  const renderShape = () => {
    const fill = `url(#${id})`;
    switch (name) {
      case "Aries":
        return (
          <g fill={fill}>
            <path d="M22 32 C22 22 30 18 38 22 C42 24 44 28 42 32 C40 28 36 26 32 28 C30 30 28 32 28 36 L28 60 L22 60 Z" />
            <path d="M58 32 C58 22 50 18 42 22 C38 24 36 28 38 32 C40 28 44 26 48 28 C50 30 52 32 52 36 L52 60 L58 60 Z" />
          </g>
        );
      case "Tauro":
        return (
          <g fill={fill}>
            <circle cx="40" cy="46" r="14" />
            <path d="M16 22 C22 28 30 32 40 32 C50 32 58 28 64 22 C58 30 50 36 40 36 C30 36 22 30 16 22 Z" />
          </g>
        );
      case "Géminis":
      case "Geminis":
        return (
          <g fill={fill}>
            <rect x="22" y="20" width="6" height="40" rx="3" />
            <rect x="52" y="20" width="6" height="40" rx="3" />
            <rect x="20" y="20" width="40" height="5" rx="2.5" />
            <rect x="20" y="55" width="40" height="5" rx="2.5" />
          </g>
        );
      case "Cáncer":
      case "Cancer":
        return (
          <g fill={fill}>
            <path d="M20 40 a14 14 0 0 1 28 0 a8 8 0 0 1 -8 8 a4 4 0 0 0 4 4" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" />
            <path d="M60 40 a14 14 0 0 1 -28 0 a8 8 0 0 1 8 -8 a4 4 0 0 0 -4 -4" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" />
          </g>
        );
      case "Leo":
        return (
          <g fill={fill}>
            <circle cx="40" cy="36" r="14" />
            <path d="M40 50 C30 56 26 64 30 70 C34 76 46 76 50 68 C53 62 50 56 44 54" stroke={fill} strokeWidth="5" fill="none" strokeLinecap="round" />
          </g>
        );
      case "Virgo":
        return (
          <g fill={fill}>
            <rect x="20" y="22" width="6" height="34" rx="3" />
            <path d="M26 28 L36 28 L36 56 L42 56 L42 28 L52 28 L52 56 L58 56 C62 56 64 52 60 48" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        );
      case "Libra":
        return (
          <g fill={fill}>
            <rect x="14" y="56" width="52" height="6" rx="3" />
            <path d="M40 24 a12 12 0 1 0 0 24" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" />
            <rect x="20" y="46" width="40" height="5" rx="2.5" />
          </g>
        );
      case "Escorpio":
        return (
          <g fill={fill}>
            <path d="M16 56 L16 28 L26 28 L26 50 L36 50 L36 28 L46 28 L46 50 L54 50 L54 36 L62 36" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M62 36 L66 30 L62 24" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        );
      case "Sagitario":
        return (
          <g fill={fill}>
            <path d="M20 60 L60 20" stroke={fill} strokeWidth="6" strokeLinecap="round" />
            <path d="M48 20 L60 20 L60 32" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M28 36 L40 48" stroke={fill} strokeWidth="6" strokeLinecap="round" />
          </g>
        );
      case "Capricornio":
        return (
          <g fill={fill}>
            <path d="M18 24 L26 56 C30 64 38 64 42 56 L48 30" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="56" cy="48" r="10" fill="none" stroke={fill} strokeWidth="6" />
          </g>
        );
      case "Acuario":
        return (
          <g fill={fill}>
            <path d="M14 28 Q22 18 30 28 T46 28 T62 28" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" />
            <path d="M14 46 Q22 36 30 46 T46 46 T62 46" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" />
          </g>
        );
      case "Piscis":
        return (
          <g fill={fill}>
            <path d="M20 22 C28 36 28 44 20 58" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" />
            <path d="M60 22 C52 36 52 44 60 58" stroke={fill} strokeWidth="6" fill="none" strokeLinecap="round" />
            <rect x="18" y="37" width="44" height="6" rx="3" />
          </g>
        );
      default:
        return <circle cx="40" cy="40" r="20" fill={fill} />;
    }
  };

  return (
    <svg viewBox="0 0 80 80" className="w-full h-full">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={c1} />
          <stop offset="100%" stopColor={c2} />
        </linearGradient>
      </defs>
      {renderShape()}
    </svg>
  );
}

const FALLBACK = [
  { name: "Aries", dates: "21 Mar - 19 Abr" },
  { name: "Tauro", dates: "20 Abr - 20 May" },
  { name: "Géminis", dates: "21 May - 20 Jun" },
  { name: "Cáncer", dates: "21 Jun - 22 Jul" },
  { name: "Leo", dates: "23 Jul - 22 Ago" },
  { name: "Virgo", dates: "23 Ago - 22 Sep" },
  { name: "Libra", dates: "23 Sep - 22 Oct" },
  { name: "Escorpio", dates: "23 Oct - 21 Nov" },
  { name: "Sagitario", dates: "22 Nov - 21 Dic" },
  { name: "Capricornio", dates: "22 Dic - 19 Ene" },
  { name: "Acuario", dates: "20 Ene - 18 Feb" },
  { name: "Piscis", dates: "19 Feb - 20 Mar" },
];

export default function ZodiacNumbers() {
  const { data } = useRecommendations();
  const signs = (data?.zodiac && data.zodiac.length === 12)
    ? data.zodiac
    : FALLBACK.map((f) => ({ ...f, number: "--" }));

  return (
    <section className="py-16 px-4 bg-gradient-to-b from-blue-50/40 to-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-semibold mb-3 border border-purple-100">
            <Sparkles className="w-3.5 h-3.5" /> NÚMEROS DE LA SUERTE
          </div>
          <h2 className="np-title inline-block text-2xl sm:text-3xl font-extrabold text-[#0e4a7a]">
            Números de la Suerte para los Signos del Zodiaco
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto mt-4 text-sm sm:text-base">
            Asignados por Prometeo tomando en cuenta la energía del día, la numerología y la vibración de cada signo.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
          {signs.map((s) => {
            const theme = SIGN_THEME[s.name] || { gradient: "from-blue-500 to-blue-700", glow: "rgba(29,108,177,0.4)", element: "" };
            const numLabel =
              typeof s.number === "number"
                ? String(s.number).padStart(2, "0")
                : (s.number || "--");
            return (
              <div
                key={s.name}
                className="np-card group relative bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden text-center transition-all hover:shadow-2xl"
                style={{ "--shadow-color": theme.glow }}
              >
                {/* Decorative gradient halo */}
                <div
                  className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-20 group-hover:opacity-40 transition-opacity"
                  style={{ background: `radial-gradient(circle, ${theme.glow}, transparent 70%)` }}
                />

                <div className="pt-6 pb-3 px-3">
                  <div className="w-20 h-20 mx-auto group-hover:scale-110 transition-transform duration-300">
                    <ZodiacIcon name={s.name} gradient={theme.gradient} />
                  </div>
                </div>

                <div className="px-3 pb-5">
                  <p
                    className={`font-extrabold text-lg bg-gradient-to-r ${theme.gradient} bg-clip-text text-transparent`}
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {s.name}
                  </p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{theme.element}</p>
                  <p className="text-[10px] text-gray-500 mt-1">{s.dates}</p>

                  <div className="mt-4 relative inline-block">
                    <div
                      className={`relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${theme.gradient} text-white font-extrabold text-2xl shadow-lg`}
                      style={{ boxShadow: `0 8px 20px -8px ${theme.glow}` }}
                    >
                      <span style={{ fontFamily: "'Playfair Display', serif" }}>{numLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
